/**
 * The ONLY seam between @rista/core and each app, as of 2026-08-27.
 *
 * It used to have a sibling, `store-bridge.ts`, covering STATE. That is gone: core owns the store
 * now, so its state readers import `…/redux-store` directly. This file covers everything left
 * that core cannot reach, and the list is TEN fields, not the four this header used to name:
 * UI (the three toasts), platform SDKs (Sentry via `logEvent`, GA/GTM via `sendGTMEvent`),
 * hardware (`printSaleTickets`, `pollForTickets`), the session teardown (`logout`), and the two
 * host reads (`env`, `window`). Each app calls `initEffectsBridge({...})` once at boot; shared
 * logic then fires effects without knowing which app it is running inside, or whether the effect
 * is a react-toastify call or a React Native one.
 *
 * ⚠️ **This is a STOPGAP by design, and the plan is to shrink it.** Every entry here is a
 * capability that could not be uplifted yet, not a capability that belongs behind an interface
 * forever. When a dependency becomes upliftable — the toast moving to `@rista/ui` with a
 * core-safe adapter, the Sentry wrapper getting a platform split, the logout helpers losing
 * their native-bridge calls — delete the entry and call the real thing. Adding an entry should
 * feel like taking on debt, because it is.
 *
 * KEEP THIS BORING. Every effect is optional and every getter
 * degrades to a no-op rather than throwing, because an interceptor must never blow up a
 * request just because an effect was not wired.
 */

/** Shows an error toast. Web: `ToastNotifyError`. Mobile: its RN toast equivalent. */
type NotifyError = (message: string) => void;

/**
 * Shows a SUCCESS toast. Web: `ToastNotifySuccess`. Mobile: its RN equivalent — both apps
 * already export exactly this name, so unlike most entries here it is a symmetric mapping
 * rather than a capability one platform lacks.
 *
 * Added session 15 for the saga phase: `outletStatus` and `notifications` both show a success
 * toast, and there is no way to express that in shared code without a seam. Still DEBT, per the
 * note at the top — when the toast moves to `@rista/ui` with a core-safe adapter, this and
 * `notifyError` are deleted together.
 */
type NotifySuccess = (message: string) => void;

/**
 * Shows the INCOMING-NOTIFICATION toast. **Not a cosmetic variant of `notifySuccess`** — on the
 * platform that wires it, this dismisses whatever notification toast is already up (only the
 * latest is kept) and starts a looping audio clip alongside it.
 *
 * Added 2026-08-25 (session 30) by user decision, to unblock the notifications listener
 * middleware on the platform that has one, and behind it the store module.
 *
 * ⚠️ **THE BLOCKER SHRANK — re-audited 2026-09-04; the reason recorded here was half wrong.**
 * This entry (and the ledger's 2026-08-27 field-by-field table) said the looping clip went
 * "through the `Audio` constructor, which core cannot reach". Core reaches it: the ringer was
 * uplifted the day before that audit and lives in `utils/notificationAudio/index.desktop.ts`,
 * which declares `Audio` locally (no DOM lib) and owns the loop, the 4-minute auto-stop and
 * `stopSound`. What is ACTUALLY left is the TOAST — `commonComponents/ToastNotify` on
 * react-toastify, dismissing its own previous toast id — so this entry dies with `notifyError`
 * and `notifySuccess` when a toast reaches `@rista/ui`, not before and not separately.
 *
 * ⚠️ Note the app's `ToastNotifyNotification` plays its OWN inline `new Audio(...)` rather than
 * calling the uplifted ringer, so core must not add a `playSound` call beside this one — that
 * would ring twice.
 *
 * ⚠️ **ONE-SIDED, like the ticket poll below.** The other platform has no notifications
 * middleware at all, so it wires nothing and this degrades to the no-op default — which again is
 * why the default matters rather than being a formality.
 */
type NotifyNotification = (message: string) => void;

/** Logs a named analytics/breadcrumb event. Mobile wires Sentry; web may no-op. */
type LogEvent = (name: string, data?: Record<string, any>) => void;

/**
 * Tears down the session.
 *
 * ⚠️ **The blocker here SHRANK — re-audited 2026-08-27, and the old reason no longer holds.**
 * This used to say web's `handleLogoutAction` "talks to a native Android bridge
 * (`sendDataToApp`)" and so could not be shared. `sendDataToApp` has been in core since the
 * `nativeHooks` uplift, and so are `isAndroid`, `history`/`LOGIN_ROUTE`, `supportLogout`,
 * `LOG_LEVELS` and `REACT_NATIVE_APP_EVENTS`. What is actually left on the web side is FIVE
 * ordinary functions still sitting in the app — `isSupportLogin`, `clearUserLoginData`,
 * `clearSupportLoginData` (`helper/commonHelpers`), `clearAppLsData` (`utils/localStorage`,
 * which preserves four keys across a clear) and `resetOfflineData` (`helper/OfflineHelper`) —
 * whose own dependencies are already in core. That is a normal uplift batch, not a capability
 * core cannot have.
 *
 * The entry stays because MOBILE's half is still blocked: `checkSupportLogout` stops a native
 * audio player, and `stopSound` on that platform is a standing REVIEWED_DIVERGENT symbol
 * (`helper/pusherNotif`, `.android`/`.ios`, BackgroundTimer) that core cannot import. Both
 * sides have to move before this field can be deleted.
 */
type Logout = () => void;

/**
 * Fires ONE product-analytics event. **This is not `logEvent`** — that one is a Sentry
 * breadcrumb; this is the GA/GTM funnel.
 *
 * Added 2026-08-23 (session 29) by user decision, to unblock `saga/recommender` and the
 * analytics half of `saga/checkout` / `saga/tableView`.
 *
 * ⚠️ **The reason this is a bridge entry and not an uplift is the IMPLEMENTATION, not the call
 * shape.** Both apps' call sites are already identical — `sendGTMEvent({ eventName, ... })` —
 * but one is 203 lines on `react-ga4` and the other 95 on its own native SDK, and neither
 * package can be a hard dependency of core. Web reaches it as a free function, mobile as a
 * method on a default-exported helper; the app wires whichever it has.
 *
 * The payload is passed through UNTOUCHED. Core must not invent a normalised event shape here —
 * the two SDKs already agree on this one, and reshaping it in the bridge would make the seam an
 * interface, which is exactly what the note at the top of this file forbids.
 *
 * ⚠️ `params` is deliberately `any`, not `Record<string, any>`. Both apps declare a NARROW
 * `params` type (~20 optional keys), and under `strictFunctionTypes` a `Record<string, any>`
 * parameter is not assignable to a narrower one — web tsc rejected the wiring outright. `any`
 * keeps each app's own type authoritative at its own call sites.
 */
type SendGTMEvent = (payload: { eventName: string; params?: any }) => void;

/**
 * Prints the tickets for a sale. Added 2026-08-23 (session 29) by user decision, to unblock
 * `saga/checkout` and `saga/tableView` on both platforms.
 *
 * ⚠️ **The largest piece of debt on this seam, and deliberately so.** Behind it sit ~1950 lines
 * of `helper/printHelper` per platform reaching USB and Bluetooth printers, Electron IPC and
 * native modules — none of which can live in a package that must stay React-Native-safe. The
 * argument list is the apps' own, identical on both sides.
 */
type PrintSaleTickets = (
  sale: any,
  tickets?: any[],
  retryCount?: any,
  isManualPrint?: boolean,
) => void;

/**
 * Kicks the background poll that drains unprinted tickets to the printers.
 *
 * ⚠️ **WEB-ONLY, and that asymmetry is real.** The other platform's saga files carry the same
 * call COMMENTED OUT, so wiring it there would start a poll that has never run. It is left
 * unwired on that platform and degrades to the no-op default — which is why the default matters
 * here rather than being a formality.
 */
type PollForTickets = () => void;

/**
 * Silences the new-order alert: web's `stopDesktopNotification` (Electron audio / NW.js IPC),
 * mobile's `stopSound` (a native audio player core cannot import). Added 2026-09-15 for
 * `myOrders/useOrderActions`, which fires it on every CTA press as both v1 cards did.
 */
type StopNewOrderAlert = () => void;

/**
 * WEB-ONLY, added 2026-09-17 for my-orders v2's own save path (D14): web's `saveMyOrderSale`
 * refreshes the adhoc-table list after a save, and mobile has no such list —
 * `helpers/adhocTableHelpers` is a `.desktop`-only module, so shared code cannot even import it.
 * Mobile leaves it at the no-op default, which is exactly what its saga did. (The back-order
 * modal that sat beside it is v2's own `sheets/Backorder` now, on both platforms.)
 */
type RefreshAdhocTables = (sale: any) => void;

export interface AppEffects {
  notifyError: NotifyError;
  notifySuccess: NotifySuccess;
  notifyNotification: NotifyNotification;
  logEvent: LogEvent;
  sendGTMEvent: SendGTMEvent;
  printSaleTickets: PrintSaleTickets;
  stopNewOrderAlert: StopNewOrderAlert;
  refreshAdhocTables: RefreshAdhocTables;
  pollForTickets: PollForTickets;
  logout: Logout;
  /**
   * Host CONFIG/env values. Not an "effect", but it belongs to the same seam and for the same
   * reason: the two apps read config from incompatible places — web from `process.env.REACT_APP_*`
   * (inlined by webpack at build time, so it cannot be read dynamically from a shared module),
   * mobile from `react-native-config` via a 114-line `config/index.js` that itself imports
   * `commonHelpers` and `data/constants`.
   *
   * Each app passes the handful of values shared code actually needs. THIRTEEN keys as of
   * 2026-09-04 — this list had drifted to eight, so re-derive it from `grep -o 'getEnv("[^"]*"'`
   * rather than trusting it after a batch:
   *   `ristaApiUrl`             — BOTH, `config.RISTA_API_URL` (the API origin)
   *   `recommendationEngineUrl` — BOTH, the V2 engine's own host (mobile joined it with the
   *                               Suggestive Selling convergence, which made one saga serve both)
   *   `dotpeApiUrl`             — BOTH, DotPe's enquiry API (web `process.env.REACT_APP_DOTPE_API_URL`,
   *                               mobile by build in `bootstrapCoreBridges.ts`)
   *   `healthServiceUrl`        — web, `process.env.REACT_APP_HEALTH_SERVICE_URL`
   *   `urlAtoken`               — web, the `?atoken=` query param, captured once at boot
   *   `subwayAccountUuid`       — BOTH, the gate behind `shouldCalculateSale`/`preventCloseSale`
   *                               (web `REACT_APP_SUBWAY_ACCOUNT_UUID`, mobile `config/index.js`)
   *   `isBetaOrProdEnv`         — BOTH, `shouldCalculateSale`'s other half (web `data/envConstants`,
   *                               mobile `config/index.js` — production, as it has no beta build)
   *   `oracleGitInfo`,          — web, build-time git values from `helper/generatedGitInfo.json`,
   *   `commonGitInfo`,            which `npm run git-info` regenerates per build INSIDE the app;
   *   `ristaPosCommonGitInfo`     read by `getCurrentVersions` for the login metadata
   *   `apiBaseUrl`              — mobile, `config.apiBaseUrl` (NOT the same as `ristaApiUrl`)
   *   `appVersion`              — mobile, `config.appVersion`
   *   `versionName`             — mobile, `mainUrl.REACT_APP_VN`, the login saga's version param
   *   `ristaItemBaseUrl`        — BOTH, the item-image CDN origin read by `catalogueHelpers`'
   *                               `getImageUrl` (web `process.env.REACT_APP_RISTA_ITEM_BASE_URL`,
   *                               mobile `mainUrl.REACT_APP_RISTA_ITEM_BASE_URL`)
   *   `paymentIconBaseUrl`      — BOTH, the payment-provider logo host read by `catalogueHelpers`'
   *                               `getPaymentIconsUrl` (`REACT_APP_RISTA_EXTERNAL_PAYMENT_ICON` on
   *                               each app; added 2026-10-02 with the kiosk payment setup)
   *
   * ⚠️ `ristaItemBaseUrl` was `itemBaseUrl`, wired by MOBILE ALONE, until 2026-09-09. Web read
   * the same value straight off `process.env` in its own copy of `getImageUrl` — which is what
   * kept that eight-line function split across two variants for nothing. Web wires the key now
   * and there is one shared body; do not re-add a mobile-only spelling.
   *
   * ✅ **Audited healthy 2026-09-04, in both directions**: every one of the 13 keys core reads is
   * wired by at least one app, and neither app wires a key core never reads. Three are wired by
   * both apps as of 2026-09-09 (`ristaItemBaseUrl` joined them), which is why 10 web + 6 mobile
   * is 13 and not 16.
   *
   * ⚠️ `getEnv` is only safe to call from inside a function that runs AFTER boot — never at a
   * core module's top level. Both apps reach `utils/apiService` through
   * `bootstrapCoreBridges`'s own import graph, so core modules evaluate BEFORE
   * `initEffectsBridge()` runs and a module-load read would see the empty default.
   */
  env: Record<string, any>;
  /**
   * The host global. Web passes `window`; mobile passes `globalThis` (React Native has no
   * `window.location`). Core must therefore treat every property as possibly absent —
   * see `getWindow()`.
   */
  window: any;
}

const noop = () => {};

let effects: AppEffects = {
  notifyError: noop,
  notifySuccess: noop,
  notifyNotification: noop,
  logEvent: noop,
  sendGTMEvent: noop,
  printSaleTickets: noop,
  stopNewOrderAlert: noop,
  refreshAdhocTables: noop,
  pollForTickets: noop,
  logout: noop,
  env: {},
  window: undefined,
};

/**
 * Call once per app, at boot, before any @rista/core logic runs.
 *
 * There is deliberately no `isEffectsBridgeReady()` companion. One existed, was never called by
 * anything in either app or in core, and was removed 2026-08-27 along with the `initialised`
 * flag it read. Callers do not need it: every accessor below degrades to a no-op or `undefined`
 * when unwired, which is the whole design of this seam.
 */
export function initEffectsBridge(appEffects: Partial<AppEffects>): void {
  effects = { ...effects, ...appEffects };
}

/**
 * ⚠️ These intentionally DO NOT throw when unwired. An effect is not load-bearing: dropping a
 * toast is strictly better than turning a successful API response into an exception inside an
 * axios interceptor.
 */
export function notifyError(message: string): void {
  effects.notifyError(message);
}

export function notifySuccess(message: string): void {
  effects.notifySuccess(message);
}

export function notifyNotification(message: string): void {
  effects.notifyNotification(message);
}

export function logEvent(name: string, data?: Record<string, any>): void {
  effects.logEvent(name, data);
}

export function sendGTMEvent(payload: { eventName: string; params?: any }): void {
  effects.sendGTMEvent(payload);
}

export function printSaleTickets(
  sale: any,
  tickets?: any[],
  retryCount?: any,
  isManualPrint?: boolean,
): void {
  effects.printSaleTickets(sale, tickets, retryCount, isManualPrint);
}

/** Refresh the adhoc-table list after a sale save. Both apps wire core's `adhocTableHelpers.updateAdhocTables` (mobile since the release_4.0.0 merge of 2026-10-05). */
export function refreshAdhocTables(sale: any): void {
  effects.refreshAdhocTables(sale);
}

export function stopNewOrderAlert(): void {
  effects.stopNewOrderAlert();
}

export function pollForTickets(): void {
  effects.pollForTickets();
}

export function logout(): void {
  effects.logout();
}

/**
 * Reads one injected config value. Returns `undefined` when unwired — same degrade-don't-throw
 * rule as the effects above, because a missing base URL should produce a failed request the
 * app's own error handling can see, not a module-load crash.
 */
export function getEnv(key: string): any {
  return effects.env?.[key];
}

/** Returns the host global, or `undefined` if the app never wired one. */
export function getWindow(): any {
  return effects.window;
}
